import { useSearch } from "@tanstack/react-router";
import { Button, Modal } from "antd";
import { useDestroyCustomDomainMutation, useGetCustomDomainQuery } from "api/TenantSlug/CustomDomain/hooks";
import { ManageCustomDomainForm } from "components/Forms/ManageCustomDomainForm/ManageCustomDomainForm.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { toast } from "react-toastify";
import { DrawerId, FormId } from "types/models.ts";

interface ManageCustomDomainDrawerProps {
    onSubmit: () => void;
}

export const ManageCustomDomainDrawer: FC<ManageCustomDomainDrawerProps> = ({ onSubmit }) => {
    const { t } = usePaldockTranslation("drawers.manage-custom-domain");
    const { t: tC } = usePaldockTranslation("common");
    const id = DrawerId.ManageCustomDomain;
    const { hide } = useDrawer();

    const searchParams: { ["manageCustomDomain-domainId"]: string | undefined } = useSearch({ strict: false });
    const domainId = searchParams["manageCustomDomain-domainId"];
    const isEditMode = Boolean(domainId);

    const { isLoading, data } = useGetCustomDomainQuery(domainId ?? "", {}, { enabled: Boolean(domainId) });
    const destroyDomainMutation = useDestroyCustomDomainMutation();

    const handleClose = () => {
        hide(id, "manageCustomDomain-domainId");
    };

    const handleSubmit = async () => {
        onSubmit?.();
        handleClose();
    };

    const handleDeleteClick = () => {
        if (!domainId) {
            return;
        }

        Modal.confirm({
            title: t("messages.confirm_deletion"),
            content: t("messages.confirm_deletion_message"),
            okText: tC("delete"),
            cancelText: tC("cancel"),
            okType: "danger",
            onOk: () => handleDelete(),
        });
    };

    const handleDelete = () => {
        if (!domainId) {
            return;
        }

        destroyDomainMutation.mutate(domainId, {
            onSuccess: () => {
                toast.success(t("messages.delete_success"));
                handleClose();
                onSubmit?.();
            },
            onError: (e: any) => {
                toast.error(`${t("messages.delete_error")}: ${(e as Error).message ?? t("messages.unknown_error")}`);
            },
        });
    };

    return (
        <Drawer
            loading={isEditMode && isLoading}
            drawerId={id}
            title={<DrawerTitle mainTitle={isEditMode ? t("titles.edit") : t("titles.create")} />}
            searchParamsToClean={["manageCustomDomain-domainId"]}
            extraButtons={
                <>
                    {isEditMode && (
                        <Button
                            danger
                            size={"large"}
                            type={"primary"}
                            onClick={handleDeleteClick}
                            loading={destroyDomainMutation.isPending}
                        >
                            {tC("delete")}
                        </Button>
                    )}
                    <Button size={"large"} type={"primary"} htmlType={"submit"} form={FormId.ManageCustomDomainForm}>
                        {tC("save")}
                    </Button>
                </>
            }
        >
            <div className={"bg-gray-100 rounded-lg border-dashed border"}>
                <ManageCustomDomainForm onSubmit={handleSubmit} domain={data?.customDomain} />
            </div>
        </Drawer>
    );
};
